import { Values } from "@/constants";
import { createContext, useContext } from "react";
import { createStore, useStore } from "zustand";
import { subscribeWithSelector } from "zustand/middleware";
import {
  TreeColumnType,
  TreeDataType,
  TreeLayoutType,
  TreeMapType,
  TreeNodeType,
  TreefetchingOptionsType,
} from "./index";

// Utility functions for summary nodes
const isSummaryNode = (id: string) => id?.endsWith("-summary") || false;
const getOriginalId = (summaryId: string) => summaryId.replace("-summary", "");
const getSummaryId = (originalId: string) => `${originalId}-summary`;

const expandTreeMapFromNode = (
  parentNode: TreeNodeType | null,
  data: TreeDataType,
  treeMap: TreeMapType,
  childrenIds: string[],
  layout?: TreeLayoutType,
): TreeMapType => {
  const newNode: TreeNodeType = {
    id: data.id,
    parentId: parentNode?.id || null,
    isExpanded: data.isExpanded || false,
    childrenIds: childrenIds,
    depth: parentNode ? parentNode.depth + 1 : 0,
  };

  // Only create child nodes that don't already exist
  const existingChildNodes = childrenIds.reduce((acc, childId) => {
    if (treeMap[childId]) {
      // Keep existing child but update its parentId if needed
      acc[childId] = {
        ...treeMap[childId],
        parentId: data.id,
      };
    } else {
      // Create new skeleton child node
      acc[childId] = {
        id: childId,
        parentId: data.id,
        isExpanded: false,
        childrenIds: [],
        depth: newNode.depth + 1,
      };
    }
    return acc;
  }, {} as TreeMapType);

  const updatedTreeMap = {
    ...treeMap,
    [data.id]: newNode,
    ...existingChildNodes,
  };

  // Add summary node if summary rows are enabled and there are children
  if (layout?.isSummaryRowVisible && childrenIds.length > 0) {
    const summaryId = getSummaryId(data.id);
    updatedTreeMap[summaryId] = {
      id: summaryId,
      parentId: data.id,
      isExpanded: false,
      childrenIds: [],
      depth: newNode.depth + 2, // Summary rows have depth+2 like in original Tree
    };
  }

  return updatedTreeMap;
};

/**
 * Mutates `treeMap` in-place: ensures skeleton child nodes exist for
 * each childId, and adds a summary node if the layout requires one.
 */
const ensureChildNodes = (
  treeMap: TreeMapType,
  parentId: string,
  childrenIds: string[],
  parentDepth: number,
  layout: TreeLayoutType,
) => {
  childrenIds.forEach((childId) => {
    if (!treeMap[childId]) {
      treeMap[childId] = {
        id: childId,
        parentId,
        isExpanded: false,
        childrenIds: [],
        depth: parentDepth + 1,
      };
    }
  });

  if (layout.isSummaryRowVisible && childrenIds.length > 0) {
    const summaryId = getSummaryId(parentId);
    if (!treeMap[summaryId]) {
      treeMap[summaryId] = {
        id: summaryId,
        parentId,
        isExpanded: false,
        childrenIds: [],
        depth: parentDepth + 2,
      };
    }
  }
};

const OVERSCAN_COLUMNS = 2;
const UPDATE_THRESHOLD_COLUMNS = 1;

const calculateVisibleRowNodes = (
  visibleIds: string[],
  scrollPositionY: number,
  containerHeight: number,
  treeMap: TreeMapType,
  verticalOverscan: number = 4,
  stickyRowCount: number = 0,
) => {
  // Calculate the scroll-based window
  const scrollStart =
    Math.floor(scrollPositionY / Values.DEFAULT_ROW_HEIGHT) -
    1 -
    verticalOverscan;
  const scrollEnd =
    Math.ceil((scrollPositionY + containerHeight) / Values.DEFAULT_ROW_HEIGHT) +
    1 +
    verticalOverscan;

  // Always include rows from 0 to stickyRowCount to handle transitions
  // when rows stop being sticky but are still in the visible area
  const start = Math.max(0, Math.min(scrollStart, stickyRowCount));
  const end = Math.min(
    visibleIds.length,
    Math.max(scrollEnd, stickyRowCount + 1),
  );

  return visibleIds
    .slice(start, end)
    .map((id, i) => ({ id, index: start + i, node: treeMap[id] }))
    .filter(({ node }) => node);
};

interface TreeStore {
  // State
  contextId: string;
  treeMap: TreeMapType;
  layout: TreeLayoutType | null;
  scrollPosition: { x: number; y: number };
  columnOffsets: { [key: string]: { left: number; width: number } };
  containerWidth: number;
  containerHeight: number;
  visibleStartColumnIndex: number;
  visibleEndColumnIndex: number;
  lastUpdateScrollX: number;
  visibleRowNodes: Array<{ id: string; index: number; node: TreeNodeType }>;
  visibleStickyRowNodes: Array<{
    id: string;
    index: number;
    node: TreeNodeType;
  }>;
  handleStickyBranchToggle: (nodeId: string, stickyIndex: number) => void;
  handleBranchToggle: (nodeId: string) => void;
  handleMouseDown: (columnKey: string) => (e: any) => void;
  columnsLockedLeft: TreeColumnType[];
  columnsLockedRight: TreeColumnType[];
  columnsUnlocked: TreeColumnType[];
  getChildrenIds: (data: TreeDataType) => string[];
  setScrollPosition: (x: number, y: number) => void;

  // Actions
  initialize: (
    layout: TreeLayoutType,
    contextId: string,
    handleMouseDown: (columnKey: string) => (e: any) => void,
    columns: TreeColumnType[],
  ) => void;
  updateNodeData: (
    id: string,
    data: TreeDataType,
    getChildrenIds: (data: TreeDataType) => string[],
  ) => void;
  toggleNodeExpansion: (
    nodeId: string,
    fetchingOptions: TreefetchingOptionsType,
  ) => void; // Pass fetchingOptions
  expandAllNodes: () => void;
  collapseAllNodes: () => void;
}

/**
 * Pure function: given the flat ordered list of visible IDs, the scroll position,
 * and the tree map, determine which ancestor rows should be rendered as sticky headers.
 *
 * A sticky row is an expanded ancestor of the row currently at the top of the viewport.
 * For example, if the topmost visible row is at depth 3, its expanded ancestors at
 * depths 0, 1, and 2 would all be sticky.
 *
 * Algorithm: starting from the topmost visible row, walk through consecutive rows in
 * the visibleIds list. For each depth level (0, 1, 2, ...), take the row at
 * (topRowIndex + depth) and find its ancestor at that depth. If the ancestor is
 * expanded, it becomes a sticky header.
 */
const calculateStickyRows = (
  visibleIds: string[],
  scrollPositionY: number,
  treeMap: TreeMapType,
): Array<{ id: string; index: number; node: TreeNodeType }> => {
  if (visibleIds.length === 0) return [];

  // The index of the row at the top of the viewport
  const topRowIndex = Math.max(
    0,
    Math.min(
      Math.floor(scrollPositionY / Values.DEFAULT_ROW_HEIGHT),
      visibleIds.length - 1,
    ),
  );

  const findAncestorAtDepth = (node: TreeNodeType, targetDepth: number) => {
    let ancestor = node;
    while (ancestor && ancestor.depth !== targetDepth) {
      ancestor = treeMap[ancestor.parentId];
    }
    return ancestor;
  };

  const stickyRows: Array<{ id: string; index: number; node: TreeNodeType }> =
    [];

  for (let depth = 0; ; depth++) {
    const rowIndex = topRowIndex + depth;
    if (rowIndex >= visibleIds.length) break;

    const node = treeMap[visibleIds[rowIndex]];
    if (!node || node.depth < depth) break;

    const ancestor = findAncestorAtDepth(node, depth);
    if (!ancestor || !ancestor.isExpanded) break;

    const ancestorIndex = visibleIds.indexOf(ancestor.id);
    stickyRows.push({ id: ancestor.id, index: ancestorIndex, node: ancestor });
  }

  return stickyRows;
};

/**
 * Pure function: computes the next visibleRowNodes and visibleStickyRowNodes
 * given the full set of inputs. No store access — all data passed explicitly.
 */
const computeVisibleRowState = (
  targetTreeMap: TreeMapType,
  layout: TreeLayoutType | null,
  scrollPositionY: number,
  containerHeight: number,
) => {
  const visibleIds = calculateVisibleIds(targetTreeMap, layout);

  const newStickyRows = calculateStickyRows(
    visibleIds,
    scrollPositionY,
    targetTreeMap,
  );

  // Use the freshly computed sticky row count (not a stale value from the store)
  // to ensure the virtualization window correctly includes rows transitioning
  // between sticky and non-sticky states
  const newVisibleRows = calculateVisibleRowNodes(
    visibleIds,
    scrollPositionY,
    containerHeight,
    targetTreeMap,
    layout?.verticalOverscan || 4,
    newStickyRows.length,
  );

  return {
    visibleRowNodes: newVisibleRows,
    visibleStickyRowNodes: newStickyRows,
  };
};

export type TreeStoreApi = ReturnType<typeof createTreeStore>;

export const TreeStoreContext = createContext<TreeStoreApi | null>(null);

export function useTreeStoreApi(): TreeStoreApi {
  const store = useContext(TreeStoreContext);
  if (!store)
    throw new Error("useTreeStore must be used within a <Tree> component");
  return store;
}

export function useTreeStore<T>(selector: (state: TreeStore) => T): T {
  const store = useTreeStoreApi();
  return useStore(store, selector);
}

export function createTreeStore() {
  return createStore<TreeStore>()(
    subscribeWithSelector((set, get) => {
      /**
       * Helper: batches a new treeMap together with recomputed visible/sticky rows
       * into a single set() call. Accepts optional extra fields to merge into the set.
       */
      const commitTreeMap = (
        newTreeMap: TreeMapType,
        layout: TreeLayoutType | null,
        extraFields?: Record<string, any>,
      ) => {
        const { scrollPosition, containerHeight } = get();
        const visibleRowState = computeVisibleRowState(
          newTreeMap,
          layout,
          scrollPosition.y,
          containerHeight,
        );
        set({ treeMap: newTreeMap, ...visibleRowState, ...extraFields });
      };

      return {
        // Initial state
        contextId: "",
        treeMap: {},
        layout: null,
        scrollPosition: { x: 0, y: 0 },
        columnOffsets: {},
        containerWidth: 800,
        containerHeight: 800,
        columnsLockedLeft: [],
        columnsLockedRight: [],
        columnsUnlocked: [],
        getChildrenIds: () => [],
        visibleStartColumnIndex: 0,
        visibleEndColumnIndex: 20, // Initial guess, will be updated
        lastUpdateScrollX: 0,
        visibleRowNodes: [],
        visibleStickyRowNodes: [],

        setScrollPosition: (x, y) => {
          const {
            columnOffsets,
            containerWidth,
            containerHeight,
            columnsUnlocked,
            visibleStartColumnIndex,
            visibleEndColumnIndex,
            lastUpdateScrollX,
            treeMap,
            layout,
          } = get();

          // Batch scroll position + visible rows into a single set()
          const visibleRowState = computeVisibleRowState(
            treeMap,
            layout,
            y,
            containerHeight,
          );
          set({ scrollPosition: { x, y }, ...visibleRowState });

          const scrollX = x;

          const findCenterIndex = (targetX: number) => {
            for (let i = 0; i < columnsUnlocked.length; i++) {
              const col = columnsUnlocked[i];
              const column = columnOffsets[col.objectKey];
              if (!column) continue;
              if (
                targetX >= column.left &&
                targetX < column.left + column.width
              ) {
                return i;
              }
            }
            return -1;
          };

          const centerScrollX = scrollX + containerWidth / 2;
          const lastCenterScrollX = lastUpdateScrollX + containerWidth / 2;

          const currentCenterIndex = findCenterIndex(centerScrollX);
          const lastCenterIndex = findCenterIndex(lastCenterScrollX);

          const shouldUpdate = () => {
            if (currentCenterIndex === -1 || lastCenterIndex === -1)
              return true;
            if (
              Math.abs(currentCenterIndex - lastCenterIndex) <
              UPDATE_THRESHOLD_COLUMNS
            )
              return false;
            return true;
          };

          if (!shouldUpdate()) return;

          // Recalculate visible columns
          let startIdx = -1;
          let endIdx = -1;

          for (let i = 0; i < columnsUnlocked.length; i++) {
            const col = columnsUnlocked[i];
            const column = columnOffsets[col.objectKey];
            if (!column) continue;
            const { left, width } = column;
            const isVisible =
              left + width > scrollX && left < scrollX + containerWidth;
            if (isVisible) {
              if (startIdx === -1) startIdx = i;
              endIdx = i;
            }
          }

          if (startIdx === -1 || endIdx === -1) {
            // keep the old values if nothing is visible
            return;
          }

          const newStart = Math.max(0, startIdx - OVERSCAN_COLUMNS);
          const newEnd = Math.min(
            columnsUnlocked.length - 1,
            endIdx + OVERSCAN_COLUMNS,
          );

          if (
            newStart !== visibleStartColumnIndex ||
            newEnd !== visibleEndColumnIndex
          ) {
            set({
              visibleStartColumnIndex: newStart,
              visibleEndColumnIndex: newEnd,
              lastUpdateScrollX: scrollX,
            });
          }
        },
        handleStickyBranchToggle: (nodeId: string) => {
          get().handleBranchToggle(nodeId);
          const {
            treeMap,
            layout,
            scrollPosition,
            containerHeight,
            setScrollPosition,
          } = get();

          // After toggling a sticky row, scroll so the node is visible below its
          // sticky ancestors. The node's depth equals the number of expanded ancestors
          // that will become sticky headers above it.
          setTimeout(() => {
            const visibleIds = calculateVisibleIds(treeMap, layout);
            const actualRowIndex = visibleIds.findIndex((id) => id === nodeId);
            if (actualRowIndex === -1) {
              // Node not found, just recalculate visible rows with current position
              setScrollPosition(scrollPosition.x, scrollPosition.y);
              return;
            }

            const node = treeMap[nodeId];
            const stickyAncestorCount = node?.depth ?? 0;
            const stickyHeadersHeight =
              stickyAncestorCount * Values.DEFAULT_ROW_HEIGHT;
            const nodeDocumentTop = actualRowIndex * Values.DEFAULT_ROW_HEIGHT;
            const nodeViewportTop = nodeDocumentTop - scrollPosition.y;

            // Only scroll if the node isn't already visible below the sticky headers
            if (
              nodeViewportTop >= stickyHeadersHeight &&
              nodeViewportTop + Values.DEFAULT_ROW_HEIGHT <= containerHeight
            ) {
              return;
            }

            // Scroll so the node appears just below its sticky ancestors
            const targetScrollY = Math.max(
              0,
              nodeDocumentTop - stickyHeadersHeight,
            );
            setScrollPosition(scrollPosition.x, targetScrollY);
          }, 0);
        },
        handleBranchToggle: (nodeId: string) => {
          const { toggleNodeExpansion, layout } = get();
          toggleNodeExpansion(nodeId, layout?.fetchingOptions?.row);
        },
        handleMouseDown: (columnKey: string) => (e: any) => null,
        // Actions
        initialize: (
          layout: TreeLayoutType,
          contextId: string,
          handleMouseDown: (columnKey: string) => (e: any) => void,
          columns: TreeColumnType[],
        ) => {
          const getChildrenIds = (data: TreeDataType) => {
            if (layout?.getChildrenIds) {
              return layout.getChildrenIds(data);
            }
            return data?.children?.map((child) => child.id) || [];
          };

          const { treeMap: existingTreeMap, contextId: existingcontextId } =
            get();
          let newTreeMap = {};

          // only update the treeMap if the heads have changed, otherwise keep the existing treeMap
          // to do this, find the objects in treeMap that have no parentId and are not summary nodes
          const heads = layout?.heads;
          const existingHeads = Object.values(existingTreeMap).filter(
            (node) => !node.parentId && !isSummaryNode(node.id),
          );
          if (
            existingcontextId === contextId &&
            existingHeads.length === heads.length &&
            existingHeads.every((head, index) => head.id === heads[index].id)
          ) {
            newTreeMap = existingTreeMap;
          } else {
            newTreeMap = layout.heads.reduce((acc, head) => {
              return expandTreeMapFromNode(
                null,
                head,
                acc,
                getChildrenIds(head) || [],
                layout,
              );
            }, {} as TreeMapType);
          }

          // Batch ALL state into a SINGLE set() — treeMap, layout, columns, AND visible rows
          commitTreeMap(newTreeMap, layout, {
            layout,
            contextId,
            handleMouseDown,
            columnsLockedLeft: columns.filter((c) => c.isLockedLeft),
            columnsLockedRight: columns.filter((c) => c.isLockedRight),
            columnsUnlocked: columns.filter(
              (c) => !c.isLockedLeft && !c.isLockedRight,
            ),
            getChildrenIds,
          });
        },
        updateNodeData: (
          id: string,
          data: TreeDataType,
          getChildrenIds: (data: TreeDataType) => string[],
        ) => {
          const { treeMap, layout } = get();
          if (isSummaryNode(id) || !layout) return;

          const childrenIds = getChildrenIds(data) || [];
          const existingNode = treeMap[id];

          if (!existingNode) {
            // Case 1: Node doesn't exist, create it with full structure
            const updatedTreeMap = expandTreeMapFromNode(
              null, // Will be set correctly by the parent-child relationship
              data,
              treeMap,
              childrenIds,
              layout,
            );
            commitTreeMap(updatedTreeMap, layout);
            return;
          }

          // Case 2: Node exists, update it and potentially create new children
          const updatedTreeMap = {
            ...treeMap,
            [id]: {
              ...existingNode,
              childrenIds: childrenIds,
            },
          };

          // Ensure all children exist in the tree as skeleton nodes
          ensureChildNodes(
            updatedTreeMap,
            id,
            childrenIds,
            existingNode.depth,
            layout,
          );

          commitTreeMap(updatedTreeMap, layout);
        },
        toggleNodeExpansion: (nodeId: string) => {
          const { treeMap, layout } = get();
          const node = treeMap[nodeId];
          if (!node || !layout) return;

          const newMap = { ...treeMap };
          // Create a new object for the toggled node to avoid mutating the original
          const target = { ...newMap[nodeId] };
          newMap[nodeId] = target;
          if (target && target.childrenIds) {
            target.isExpanded = !target.isExpanded;

            // When expanding, create skeleton child nodes if they don't exist
            if (target.isExpanded && target.childrenIds.length > 0) {
              ensureChildNodes(
                newMap,
                target.id,
                target.childrenIds,
                target.depth,
                layout,
              );
            }
          }

          commitTreeMap(newMap, layout);
        },
        expandAllNodes: () => {
          const newTreeMap = Object.entries(get().treeMap).reduce(
            (acc, [id, node]) => {
              if (node.childrenIds && node.childrenIds.length > 0) {
                acc[id] = { ...node, isExpanded: true };
              } else {
                acc[id] = node;
              }
              return acc;
            },
            {} as TreeMapType,
          );

          commitTreeMap(newTreeMap, get().layout);
        },
        collapseAllNodes: () => {
          const { treeMap, layout } = get();
          const newMap = Object.entries(treeMap).reduce((acc, [id, node]) => {
            if (node.id !== "cash-flow-root" && node.isExpanded) {
              acc[id] = { ...node, isExpanded: false };
            } else {
              acc[id] = node;
            }
            return acc;
          }, {} as TreeMapType);
          commitTreeMap(newMap, layout);
        },
      };
    }),
  );
}

export const calculateVisibleIds = (
  treeMap: TreeMapType,
  layout: TreeLayoutType | null,
): string[] => {
  if (Object.keys(treeMap).length === 0 || !layout) {
    return [];
  }

  const result: string[] = [];
  function walk(id: string) {
    const node = treeMap[id];
    if (!node) return;

    if (isSummaryNode(id)) return;

    result.push(id);
    if (node.isExpanded && node.childrenIds && node.childrenIds.length > 0) {
      const existingChildren = node.childrenIds.filter(
        (childId) => treeMap[childId],
      );
      existingChildren.forEach(walk);

      const nonSummaryChildren = existingChildren.filter(
        (childId) => !isSummaryNode(childId),
      );
      if (layout.isSummaryRowVisible && nonSummaryChildren.length > 0) {
        const summaryId = getSummaryId(id);
        if (treeMap[summaryId]) {
          result.push(summaryId);
        }
      }
    }
  }

  const rootNodes = Object.values(treeMap).filter(
    (node: TreeNodeType) => !node.parentId && !isSummaryNode(node.id),
  );
  rootNodes.forEach((rootNode: TreeNodeType) => walk(rootNode.id));

  return result;
};

// Debug selector to help identify inconsistencies
export const useTreeDebugInfo = () =>
  useTreeStore((state) => ({
    treeMapSize: Object.keys(state.treeMap).length,
    visibleIdsLength: calculateVisibleIds(state.treeMap, state.layout).length,
    expandedNodes: Object.values(state.treeMap).filter(
      (node: TreeNodeType) => node.isExpanded,
    ).length,
    layout: state.layout,
  }));
